import React, { useEffect, useState } from "react";
import MainLayout from "./MainLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Paper,
  Box,
  Button,
  IconButton,
  Grid,
  InputAdornment,
  Checkbox,
  TableSortLabel,
} from "@mui/material";
import Loader from "../utils/Loader";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import TableChartIcon from "@mui/icons-material/TableChart";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";

import { BlockOutlined, Search } from "@mui/icons-material";
import { fetchProjectReport } from "../../redux/slices/projectSlice";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import UserService from "../../Services/UserList";
import Swal from "sweetalert2";
import { fetchUserOption } from "../../redux/slices/clientSlice";
import ExtractAllButton from "../utils/ExtractAllButton";
import RoleSelect from "../utils/RoleSelect";
import { ClickableTableLink } from "./ClickableTableLink";
import MonthFY from "../MonthFY";
import YearFY from "../YearFY";
import FilterBar2 from "./admin_dashboard/FilterBar2";
import CollectionReportAnalysis from "../utils/CollectionReportAnalysis";

const roleOpt = [
  { label: "IC", value: 2 },
  { label: "BSP", value: 3 },
  { label: "DSP", value: 4 },
  { label: "BD", value: 5 },
  { label: "Sales Enabler", value: 6 },
  { label: "Dealer", value: 7 },
  { label: "Studio", value: 8 },
];

const FY_YEAR_OPTIONS = [
  { label: "2026-2027", value: "2026" },
  { label: "2025-2026", value: "2025" },
  { label: "2024-2025", value: "2024" },
  { label: "2023-2024", value: "2023" },
];

const QuotationReport = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState([]);
  const [roleId, setRoleId] = useState([]);
  const [channelOpt, setChannelOpt] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);

  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  const [collectionOpt, setCollectionOpt] = useState([]);
  const { userOption } = useSelector((state) => state.client);
  const userOpt = userOption
    ? userOption.map((item) => ({
      value: item.id,
      label: `${item.name} ${item.last_name}`,
    }))
    : [];
  const { state } = useLocation();

  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (state && state.months) return state.months;
    if (state && state.month) return state.month;

    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);

    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    if (state && state.years) {
      const yearsArray = Array.isArray(state.years) ? state.years.map(String) : [String(state.years)];
      return FY_YEAR_OPTIONS.filter((y) => yearsArray.includes(y.value));
    }
    const currentYrStr = String(new Date().getFullYear());
    return FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr);
  });
  const [sortBy, setSortBy] = useState({
    sort_by: "",
    order: "desc",
  });

  const dispatch = useDispatch();
  const { projectReportData, status, count, clientOpt, stats } = useSelector(
    (state) => state.projects
  );

  const counts = count || 0;

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const currentFilters = {
    search: searchTerm,
    from: selectedFromDate || "",
    to: selectedToDate || "",
    user: selectedUsers?.map((u) => u.value)?.join(','),
    client: selectedClients?.map((u) => u.value),
    collection: selectedCollection?.map((u) => u.value),
    month: selectedMonth?.map((y) => y).join(','),
    year: selectedYear?.map((y) => y.value).join(","),
    sort_by: sortBy.sort_by,
    sort_order: sortBy.order,
    role: roleId?.map((r) => r.value),
    channel: selectedChannels?.map((u) => u.value),
  };

  useEffect(() => {
    UserService.GetChannels().then(response => {
      if (response && response.data) {
        setChannelOpt(response.data.map(ch => ({
          value: ch.id,
          label: ch.channel_name
        })));
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const isUsingDateRange = selectedFromDate || selectedToDate;
    if (!isUsingDateRange && (selectedYear?.length === 0 || selectedMonth?.length === 0)) {
      return;
    }
    dispatch(
      fetchProjectReport({
        search: searchTerm,
        from: selectedFromDate || "",
        to: selectedToDate || "",
        user: selectedUsers?.map((u) => u.value)?.join(','),
        client: selectedClients?.map((u) => u.value),
        channel: selectedChannels?.map((u) => u.value),
        collection: selectedCollection?.map((u) => u.value),
        month: selectedMonth?.map((y) => y).join(','),
        year: selectedYear?.map((y) => y.value).join(","),
        page: page,
        rowsPerPage: rowsPerPage,
        sort_by: sortBy.sort_by,
        sort_order: sortBy.order,
        role: roleId?.map((r) => r.value),
      })
    );
  }, [
    searchTerm,
    selectedClients,
    selectedUsers,
    selectedChannels,
    selectedCollection,
    page,
    rowsPerPage,
    selectedFromDate,
    selectedToDate,
    sortBy,
    roleId,
    selectedMonth,
    selectedYear,
  ]);

  const handleDownloadExcel = () => {
    setLoading(true);
    const table = document.getElementById("exceldata");
    const rows = table.querySelectorAll("tr");
    const data = [];
    rows.forEach((row, index) => {
      if (index === 0) return;

      const cells = row.querySelectorAll("td");

      const rowData = [
        cells[0]?.innerText || "N/A",
        cells[1]?.innerText || "N/A",
        cells[2]?.innerText || "N/A",
        cells[3]?.innerText || "N/A",
        cells[4]?.innerText || "N/A",
        cells[5]?.innerText || "N/A",
        cells[6]?.innerText || "N/A",
        cells[7]?.innerText || "N/A",
        cells[8]?.innerText || "N/A",
        cells[9]?.innerText || "N/A",
        cells[10]?.innerText || "N/A",
        cells[11]?.innerText || "N/A",
        cells[12]?.innerText || "N/A",
        cells[13]?.innerText || "N/A",
        cells[14]?.innerText || "N/A",
        cells[15]?.innerText || "N/A",
        cells[16]?.innerText || "N/A",
        cells[17]?.innerText || "N/A",
        cells[18]?.innerText || "N/A",
        cells[19]?.innerText || "N/A",
        cells[20]?.innerText || "N/A",
        cells[21]?.innerText || "N/A",
        cells[22]?.innerText || "N/A",
        cells[23]?.innerText || "N/A",
      ];
      data.push(rowData);
    });

    const sheetData = [
      [
        "SL No.",
        "Collection",
        "SKU Code",
        "Brand",
        "Description",
        "Finish",
        "Category",
        "Quantity",
        "MRP",
        "SKU Total",
        "Quotation ID",
        "Client",
        "Client's Region",
        "Client's State",
        "Channel",
        "User Name",
        "User's Region",
        "User's State",
        "Role",
        "Valid Upto",
        "Stage",
        "Created At",
        "Month",
        "Year",
      ],
      ...data,
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Quotation Report");

    XLSX.writeFile(wb, "quotation_report.xlsx");

    setLoading(false);
  };

  const isUsingDateRange = Boolean(selectedFromDate || selectedToDate);
  const hasValidFilters = isUsingDateRange || Boolean(selectedYear?.length > 0 && selectedMonth?.length > 0);

  const filterConfigs = [
    { id: "client", label: "Client", options: clientOpt || [], selected: selectedClients },
    { id: "user", label: "User", options: userOpt || [], selected: selectedUsers },
    { id: "collection", label: "Collection", options: collectionOpt || [], selected: selectedCollection },
    { id: "role", label: "Role", options: roleOpt || [], selected: roleId },
    { id: "channel", label: "Channel", options: channelOpt || [], selected: selectedChannels },
    { id: "year", label: "Year (FY)", options: FY_YEAR_OPTIONS, selected: selectedYear },
  ];

  const handleMonthChange = (newMonths) => {
    setSelectedMonth(newMonths);
    if (newMonths?.length > 0 && selectedYear?.length === 0) {
      const currentYrStr = String(new Date().getFullYear());
      setSelectedYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
    }
  };

  const handleFilterChange = (filterId, nextSelected) => {
    switch (filterId) {
      case "client": setSelectedClients(nextSelected); break;
      case "user": setSelectedUsers(nextSelected); break;
      case "collection": setSelectedCollection(nextSelected); break;
      case "role": setRoleId(nextSelected); break;
      case "channel": setSelectedChannels(nextSelected); break;
      case "year": 
        setSelectedYear(nextSelected); 
        if (nextSelected.length === 0) {
          setSelectedMonth([]);
        } else if (selectedMonth?.length === 0) {
          const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
          const currentCalendarMonth = new Date().getMonth() + 1;
          const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
          setSelectedMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
        }
        break;
      default: break;
    }
  };

  const handleClearFilters = () => {
    setSelectedUsers([]);
    setSelectedClients([]);
    setSelectedCollection([]);
    setSelectedChannels([]);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);

    setSelectedMonth(fullFYMonths.slice(0, currentMonthIndex + 1));
    const currentYrStr = String(new Date().getFullYear());
    setSelectedYear(FY_YEAR_OPTIONS.filter((y) => y.value === currentYrStr));
    setSearchTerm("");
    setRoleId([]);
  };

  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

  const productCollectionApi = async () => {
    try {
      const response = await UserService.collection();
      setCollectionOpt(
        response.data
          ? response.data.map((item) => ({
            value: item.name,
            label: item.name,
          }))
          : []
      );
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: error.message,
        timer: 3000,
      });
    }
  };



  useEffect(() => {
    productCollectionApi();
    dispatch(fetchUserOption());
  }, []);

  return (
    <>
      <MainLayout title={'Quotation Report'} disableScroll={true}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, pt: 1, flexShrink: 0 }}>
            <CollectionReportAnalysis
              totalQuotation={hasValidFilters ? (stats?.quotationIds || 0) : 0}
              totalCollection={hasValidFilters ? (stats?.collections || 0) : 0}
              totalUser={hasValidFilters ? (stats?.usernames || 0) : 0}
              totalClient={hasValidFilters ? (stats?.clients || 0) : 0}
              totalQty={hasValidFilters ? (stats?.totalQtySum || stats?.totalQty || 0) : 0}
              loading={hasValidFilters && (status === 'pending' || status === 'idle')}
            />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <FilterBar2
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          selectedMonth={selectedMonth}
          setSelectedMonth={handleMonthChange}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedFromDate={selectedFromDate}
          setSelectedFromDate={setSelectedFromDate}
          selectedToDate={selectedToDate}
          setSelectedToDate={setSelectedToDate}
          onClearFilters={handleClearFilters}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                startIcon={<TableChartIcon />}
                onClick={handleDownloadExcel}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2e7d32",
                  py: '2px',
                  px: '14px',
                  borderRadius: 12,
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#1b5e20" },
                }}
              >
                Excel
              </Button>

              <ExtractAllButton
                filters={currentFilters}
                totalCount={hasValidFilters ? counts : 0}
                disabled={!hasValidFilters || counts === 0}
              />
            </div>
          }
        />
        </Box>

        <Paper elevation={0} sx={{ mt: 2, borderRadius: "12px", overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader id="exceldata" sx={{
              minWidth: 650,
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#1e293b !important",
                color: "#f8fafc !important",
                fontWeight: 600,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                whiteSpace: "nowrap",
                borderBottom: "none",
                py: 2,
              },
              "& .MuiTableBody-root .MuiTableRow-root": {
                transition: "all 0.2s ease-in-out"
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: "#f8fafc"
              },
              "& .MuiTableCell-root": {
                borderBottom: "1px solid #e2e8f0",
                color: "#334155",
                fontSize: "0.875rem"
              }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    SL No.
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Collection
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    SKU Code
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Brand
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Finish
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Category
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "usage"}
                      direction={
                        sortBy.sort_by === "usage" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("usage")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      Quantity
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "mrp"}
                      direction={
                        sortBy.sort_by === "mrp" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("mrp")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      MRP
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "total"}
                      direction={
                        sortBy.sort_by === "total" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("total")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      SKU Total
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Quotation ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "client"}
                      direction={
                        sortBy.sort_by === "client" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("client")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      Client
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Client's Region
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Client's State
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Channel
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "username"}
                      direction={
                        sortBy.sort_by === "username" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("username")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      Username
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    User's Region
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    User's State
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "role_id"}
                      direction={
                        sortBy.sort_by === "role_id" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("role_id")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      Role
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "valid_upto"}
                      direction={
                        sortBy.sort_by === "valid_upto" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("valid_upto")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      Valid Upto
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Stage
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy.sort_by === "created_at"}
                      direction={
                        sortBy.sort_by === "created_at" ? sortBy.order : "desc"
                      }
                      onClick={() => handleSort("created_at")}
                      hideSortIcon={false}
                      sx={{
                        color: "white !important",
                        cursor: "pointer",
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                          color: "white !important",
                        },
                      }}
                    >
                      Created At
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Month
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Year
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const isUsingDateRange = selectedFromDate || selectedToDate;
                  const hasValidFilters = isUsingDateRange || (selectedYear?.length > 0 && selectedMonth?.length > 0);
                  if (!hasValidFilters) {
                    return (
                      <TableRow>
                        <TableCell colSpan={25} align="center">
                          No data
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return (status === "pending" || loading) ? (
                    <TableBodySkeleton cols={22} rows={10} />
                  ) : projectReportData?.length > 0 ? (
                  projectReportData.map(
                    (
                      {
                        id,
                        client_name,
                        client_region,
                        client_state,
                        username,
                        user_channel,
                        user_region,
                        user_state,
                        QuotationId,
                        valid_date,
                        created_at,
                        sku_code,
                        status,
                        brand,
                        rolename,
                        role,
                        state,
                        city,
                        usages,
                        mrp,
                        total,
                        finish,
                        category,
                        description,
                        collection,
                        user_created_at,
                        client_created_at,
                      },
                      index
                    ) => (
                      <TableRow key={id}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{collection}</TableCell>
                        <TableCell><ClickableTableLink path="/admin/product" displayValue={sku_code} searchValue={sku_code} /></TableCell>
                        <TableCell>{brand}</TableCell>
                        <TableCell>{description}</TableCell>
                        <TableCell>{finish}</TableCell>
                        <TableCell>{category}</TableCell>
                        <TableCell>{usages}</TableCell>
                        <TableCell>{mrp !== undefined && mrp !== null ? Number(mrp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}</TableCell>
                        <TableCell>{total !== undefined && total !== null ? Number(total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "N/A"}</TableCell>
                        <TableCell><ClickableTableLink path="/admin/projects" displayValue={QuotationId} searchValue={QuotationId} date={created_at ? created_at.substring(0, 4) : null} /></TableCell>
                        <TableCell><ClickableTableLink path="/admin/clientlist" displayValue={client_name} searchValue={client_name} date={client_created_at ? client_created_at.substring(0, 4) : null} /></TableCell>
                        <TableCell>{client_region}</TableCell>
                        <TableCell>{client_state}</TableCell>
                        <TableCell>{user_channel || "N/A"}</TableCell>
                        <TableCell><ClickableTableLink path="/admin/userlist" displayValue={username} searchValue={username} date={user_created_at ? user_created_at.substring(0, 4) : null} /></TableCell>
                        <TableCell>{user_region}</TableCell>
                        <TableCell>{user_state}</TableCell>
                        <TableCell>{rolename}</TableCell>

                        <TableCell
                          style={{
                            color: (() => {
                              const validDate = new Date(valid_date);
                              const currentDate = new Date();
                              currentDate.setHours(0, 0, 0, 0);
                              validDate.setHours(0, 0, 0, 0);
                              if (
                                validDate.toDateString() ===
                                currentDate.toDateString()
                              ) {
                                return "green";
                              } else if (validDate < currentDate) {
                                return "red";
                              } else if (
                                validDate.toDateString() ===
                                new Date(
                                  currentDate.setDate(currentDate.getDate() + 1)
                                ).toDateString()
                              ) {
                                return "black";
                              }
                              return "black";
                            })(),
                          }}
                        >
                          {valid_date ? new Date(valid_date).toLocaleDateString("en-GB") : 'N/A'}
                        </TableCell>

                        <TableCell>{status}</TableCell>
                        <TableCell>
                          {new Date(created_at).toLocaleString("en-GB")}
                        </TableCell>
                        <TableCell>
                          {new Date(created_at).toLocaleString("default", { month: "short" })}
                        </TableCell>
                        <TableCell>
                          {new Date(created_at).getFullYear()}
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={24} align="center">
                      No quotations found
                    </TableCell>
                  </TableRow>
                );
                })()}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={hasValidFilters ? [10, 25, 100, 500, counts] : [10, 25, 100, 500]}
            component="div"
            count={hasValidFilters ? (counts || 0) : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              backgroundColor: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              color: "#475569",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                fontWeight: 600,
                fontSize: "0.85rem",
              }
            }}
          />
        </Paper>
      </MainLayout>
    </>
  );
};

export default QuotationReport;
